import { z } from 'zod';
import type { TaskPageResponse } from '@erp/rpc';
import type { ColumnConfigEntry } from '@erp/shared-types';
import type { TasksQueueSearch } from '../../tasks/taskQueueSearch';

/**
 * Pure tab rules for a task page that shows several task types. Each task
 * type is a tab; the active one lives in `?tab=<typeKey>`. A page with one
 * type renders no tab strip and keeps its pre-tabs URL and column key.
 */

/** `?tab=` on `/pages/$taskPageId`: the active tab's task type key. */
export const taskPageTabSearchSchema = z.object({
  tab: z.string().optional(),
});

export type TaskPageTabSearch = z.infer<typeof taskPageTabSearchSchema>;

export interface TaskPageTab {
  /** Task type key — the `?tab=` value and the column key suffix. */
  key: string;
  taskTypeId: string;
  /** The type's own name (the queue's pinned type). */
  typeName: string;
  /** Tab text: the admin label, else the type name, else the key. */
  title: string;
  /** Admin default columns for this tab; null = built-in set. */
  defaultColumns: ColumnConfigEntry[] | null;
}

type TaskPageTaskTypeEntry = TaskPageResponse['taskTypes'][number];

function toTab(entry: TaskPageTaskTypeEntry): TaskPageTab {
  return {
    key: entry.key,
    taskTypeId: entry.taskTypeId,
    typeName: entry.name,
    title: entry.label || entry.name || entry.key,
    defaultColumns: entry.defaultColumns,
  };
}

/** The page's tabs in order. Never empty: falls back to the primary type. */
export function taskPageTabs(
  page: TaskPageResponse,
): [TaskPageTab, ...TaskPageTab[]] {
  const [first, ...rest] = page.taskTypes.map(toTab);
  if (first) return [first, ...rest];
  return [
    toTab({
      taskTypeId: page.taskTypeId,
      key: page.taskTypeKey,
      name: page.taskTypeName,
      label: null,
      defaultColumns: page.defaultColumns,
    }),
  ];
}

/** More than one tab: render the tab strip and per-tab URLs/column keys. */
export function hasTaskPageTabs(tabs: readonly TaskPageTab[]): boolean {
  return tabs.length > 1;
}

/**
 * The tab `?tab=` names, or the first tab when it is missing or unknown (a
 * stale link to a type the page no longer shows).
 */
export function resolveActiveTab<Tab extends TaskPageTab>(
  tabs: readonly [Tab, ...Tab[]],
  tabParam: string | undefined,
): Tab {
  return tabs.find((tab) => tab.key === tabParam) ?? tabs[0];
}

/**
 * Saved-column key for a tab. Single-type pages keep the pre-tabs key so
 * users' saved columns survive; each tab of a multi-type page gets its own.
 */
export function taskPageQueueTableId(
  pageId: string,
  tabKey: string,
  multiType: boolean,
): string {
  return multiType
    ? `task-page-queue:${pageId}:${tabKey}`
    : `task-page-queue:${pageId}`;
}

/**
 * The queue's search with the active tab kept in the URL. Single-type pages
 * never write `?tab=`.
 */
export function withActiveTab(
  search: TasksQueueSearch,
  tabKey: string,
  multiType: boolean,
): TasksQueueSearch & TaskPageTabSearch {
  return multiType ? { ...search, tab: tabKey } : search;
}

/**
 * Search for switching to another tab: only `?tab=`, so the new tab's queue
 * starts from the page's default filters (page 1, no stale search text).
 */
export function tabChangeSearch(tabKey: string): TaskPageTabSearch {
  return { tab: tabKey };
}

/** Narrow an untyped tab-change value to one of the page's tab keys. */
export function tabKeyFromValue(
  tabs: readonly TaskPageTab[],
  value: unknown,
): string | null {
  return tabs.find((tab) => tab.key === value)?.key ?? null;
}
